import type { Endpoint } from './endpoint';
import type { RouteQuery } from '../types/RouteQuery';

/** Every field null — callers spread this and override just the pair(s) they populate. */
export const EMPTY_ROUTE_QUERY: RouteQuery = {
  origin_lat: null,
  origin_lon: null,
  origin_stop_id: null,
  origin_address: null,
  dest_lat: null,
  dest_lon: null,
  dest_stop_id: null,
  dest_address: null,
  time: null,
  max_transfers: null,
  block_lines: null,
  block_modes: null,
};

/** Additional inputs `buildRouteQuery` accepts alongside the two endpoints. */
export interface QueryExtras {
  /** Wall-clock departure time as `HH:MM`, or `null` for the server default. */
  time?: string | null;
  /** Comma-separated blocked lines (e.g. `"S3,U2"`), or `null` for none. */
  blockLines?: string | null;
  /** Comma-separated blocked modes (e.g. `"Bus,Tram"`), or `null` for none. */
  blockModes?: string | null;
}

/** Position endpoint half. Extracted so `endpointPart` stays under cognitive complexity 4. */
type PositionPart = Pick<RouteQuery, 'origin_lat' | 'origin_lon' | 'dest_lat' | 'dest_lon'>;

/** Stop endpoint half. */
type StopPart = Pick<RouteQuery, 'origin_stop_id' | 'dest_stop_id'>;

/** Address endpoint half. */
type AddressPart = Pick<RouteQuery, 'origin_address' | 'dest_address'>;

/** Encodes an `Endpoint` into the two-field slice `RouteQuery` expects for that side. */
export function endpointPart(
  endpoint: Endpoint,
  prefix: 'origin' | 'dest',
): Partial<PositionPart & StopPart & AddressPart> {
  if (endpoint.kind === 'stop') return { [`${prefix}_stop_id`]: endpoint.stopId };
  if (endpoint.kind === 'address') return { [`${prefix}_address`]: endpoint.address };
  return { [`${prefix}_lat`]: endpoint.latitude, [`${prefix}_lon`]: endpoint.longitude };
}

/** Builds a fully-formed `RouteQuery` from an origin, destination, and optional extras. */
export function buildRouteQuery(
  origin: Endpoint,
  destination: Endpoint,
  extras: QueryExtras = {},
): RouteQuery {
  return {
    ...EMPTY_ROUTE_QUERY,
    time: extras.time ?? null,
    block_lines: extras.blockLines ?? null,
    block_modes: extras.blockModes ?? null,
    ...endpointPart(origin, 'origin'),
    ...endpointPart(destination, 'dest'),
  };
}
